import { useState } from 'react';
import type { UserProfile, Client } from '../page';

interface UserModalProps {
  user: UserProfile | null;
  clients: Client[];
  onClose: () => void;
  saving?: boolean;
  saveError?: string | null;
  onSave: (data: {
    full_name: string;
    email: string;
    phone: string;
    role: string;
    is_active: boolean;
    client_id: string | null;
    password?: string;
  }) => void;
}

const ROLES = [
  { value: 'admin', label: 'Admin', desc: 'Acesso total ao sistema' },
  { value: 'client', label: 'Cliente', desc: 'Acesso ao portal do cliente' },
  { value: 'member', label: 'Membro', desc: 'Acesso limitado a projetos vinculados' },
];

export default function UserModal({ user, clients, onClose, onSave, saving = false, saveError }: UserModalProps) {
  const isNew = !user;
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [role, setRole] = useState(user?.role || 'client');
  const [isActive, setIsActive] = useState(user?.is_active ?? true);
  const [clientId, setClientId] = useState(user?.client_id || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim() || !email.trim()) return;
    if (isNew && password.length < 6) return;

    onSave({
      full_name: fullName,
      email,
      phone,
      role,
      is_active: isActive,
      client_id: clientId || null,
      ...(isNew ? { password } : {}),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div>
            <h2 className="text-slate-800 font-semibold text-base">
              {isNew ? 'Novo Usuário' : 'Editar Usuário'}
            </h2>
            <p className="text-slate-400 text-xs mt-0.5">
              {isNew ? 'Crie um novo acesso ao sistema' : user.email}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Nome */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Nome completo *</label>
            <input
              type="text"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              placeholder="Nome do usuário"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300"
              required
            />
          </div>

          {/* E-mail */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">E-mail *</label>
            {isNew ? (
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="email@exemplo.com"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300"
                required
              />
            ) : (
              <>
                <input
                  type="email"
                  value={user.email}
                  disabled
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-400 bg-slate-50 cursor-not-allowed"
                />
                <p className="text-slate-400 text-xs mt-1">O e-mail não pode ser alterado aqui</p>
              </>
            )}
          </div>

          {/* Senha (somente criação) */}
          {isNew && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Senha *</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="w-full px-3 pr-10 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                >
                  <i className={showPassword ? 'ri-eye-off-line text-sm' : 'ri-eye-line text-sm'}></i>
                </button>
              </div>
              {password.length > 0 && password.length < 6 && (
                <p className="text-red-400 text-xs mt-1">A senha deve ter pelo menos 6 caracteres</p>
              )}
            </div>
          )}

          {/* Telefone */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Telefone</label>
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="(11) 99999-9999"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300"
            />
          </div>

          {/* Papel */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">Papel no sistema</label>
            <div className="space-y-2">
              {ROLES.map(r => (
                <label
                  key={r.value}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    role === r.value ? 'border-cyan-300 bg-cyan-50' : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={r.value}
                    checked={role === r.value}
                    onChange={() => setRole(r.value)}
                    className="text-cyan-500 focus:ring-cyan-300 cursor-pointer"
                  />
                  <div>
                    <p className="text-slate-700 text-sm font-medium">{r.label}</p>
                    <p className="text-slate-400 text-xs">{r.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Cliente vinculado */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Cliente vinculado</label>
            <select
              value={clientId}
              onChange={e => setClientId(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer"
            >
              <option value="">Nenhum</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
            <div>
              <p className="text-slate-700 text-sm font-medium">Usuário ativo</p>
              <p className="text-slate-400 text-xs">Usuários inativos não conseguem fazer login</p>
            </div>
            <button
              type="button"
              onClick={() => setIsActive(!isActive)}
              className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${isActive ? 'bg-emerald-500' : 'bg-slate-200'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${isActive ? 'translate-x-5' : 'translate-x-0'}`}></span>
            </button>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 shrink-0">
          {saveError && (
            <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs">
              <i className="ri-error-warning-line shrink-0"></i>
              {saveError}
            </div>
          )}
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving || !fullName.trim() || !email.trim() || (isNew && password.length < 6)}
              className="flex items-center gap-1.5 px-5 py-2 bg-cyan-500 hover:bg-cyan-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin"></i>Salvando...</>
              ) : isNew ? (
                <><i className="ri-user-add-line"></i>Criar Usuário</>
              ) : (
                'Salvar alterações'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
